/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { useCallback } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { modalShellShared } from '../../shared/modals/modalShellShared.styles';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader';
import { useModalBackdropPadding } from '../../shared/ui/hooks-ui/useModalBackdropPadding';
import { useOperationalFilterLayout } from '../../shared/filters/useOperationalFilterLayout';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import {
  formatSaleCreatedAt,
  formatSaleChannelLabel,
  formatSaleDisplayRef,
} from '../application/salePresentation';
import { SaleDetailContent } from './SaleDetailContent';
import { SentryErrorBoundary } from '../../shared/sentry/SentryErrorBoundary';
import { SaleDetailMpQrModal } from './SaleDetailMpQrModal';
import { VoidSaleConfirmModal } from './VoidSaleConfirmModal';
import { useSaleDetail } from '../application/useSaleDetail';
import { useSaleCustomerCollect } from '../application/useSaleCustomerCollect';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { useAuthStore } from '../../auth/store/useAuthStore';

type Props = {
  visible: boolean;
  saleId: string | null;
  branchId: string | null;
  onClose: () => void;
  onDetailMutated?: () => void;
};

export function SaleDetailModal({
  visible,
  saleId,
  branchId,
  onClose,
  onDetailMutated,
}: Props) {
  const backdropPadding = useModalBackdropPadding();
  const { variant: layoutVariant } = useOperationalFilterLayout();
  const isMobile = layoutVariant === 'mobile';
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();

  const {
    detail,
    loading: detailLoading,
    diningTableName,
    paymentMethodNames,
    paymentMethodNamesLoaded,
    canVoid,
    requireSupervisorPinForVoid,
    canCollectMpQr,
    mpQrModalVisible,
    openMpQrModal,
    closeMpQrModal,
    voidLoading,
    voidConfirmVisible,
    voidSucceeded,
    customerVoidPlan,
    customerPreviewLoading,
    onVoidPress,
    onVoidConfirm,
    onVoidConfirmClose,
    refresh,
    onMpQrTerminal,
    canReprint,
    reprintLoading,
    onReprintPress,
  } = useSaleDetail({
    saleId: saleId ?? '',
    branchId: branchId ?? undefined,
    onDetailMutated: () => {
      onDetailMutated?.();
    },
  });

  const { canCollectCustomer, onCollectCustomer } = useSaleCustomerCollect(detail);

  const hasCommissionsCapability = useHasBusinessCapability('commissions');
  const commissionsPermission = useBranchPermission('Commissions');
  const sessionUser = useAuthStore((s) => s.session?.user ?? null);
  const showCommissions = hasCommissionsCapability && commissionsPermission.canView;
  const showAttribution = hasCommissionsCapability;
  const canViewOthersCommissions = commissionsPermission.canViewOthers;

  const onViewLinkedOrder = useCallback(() => {
    const orderId = detail?.customerOrderId;
    if (!orderId) return;
    onClose();
    (navigation as { navigate: (name: string, params: { orderId: string }) => void }).navigate(
      'OnlineOrderDetail',
      { orderId }
    );
  }, [detail?.customerOrderId, navigation, onClose]);

  if (!visible) return null;

  // Header ticket (proto ventas-ux-v1): kicker VENTAS · title "Venta #ref".
  const detailTitle = detail
    ? `Venta ${formatSaleDisplayRef({ saleId: detail.id, counterNumber: detail.counterNumber })}`
    : 'Venta';
  const detailSubtitle = detail
    ? [
        formatSaleCreatedAt(detail.createdAt),
        formatSaleChannelLabel(detail.channel),
        detail.cashierDisplayName ? `Operador: ${detail.cashierDisplayName}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : null;

  const subModals = (
    <>
      <SaleDetailMpQrModal
        detail={detail}
        visible={mpQrModalVisible}
        onClose={closeMpQrModal}
        onTerminal={onMpQrTerminal}
        onVoidSale={onVoidPress}
      />
      <VoidSaleConfirmModal
        visible={voidConfirmVisible}
        saleTotal={detail?.total ?? 0}
        saleRefLabel={detail ? formatSaleDisplayRef({ saleId: detail.id, counterNumber: detail.counterNumber }) : null}
        success={voidSucceeded}
        loading={voidLoading}
        isFiscalSale={detail?.fiscalMode === 'fiscal'}
        hasMercadoPagoQrPayment={Boolean(detail?.payments.some((p) => p.methodId === 'mercadopago_qr'))}
        hasCommissions={hasCommissionsCapability}
        customerVoidPlan={customerVoidPlan}
        customerPreviewLoading={customerPreviewLoading}
        onClose={onVoidConfirmClose}
        requireSupervisorPin={requireSupervisorPinForVoid}
        onConfirm={(reason, pin) => onVoidConfirm(reason, pin)}
      />
    </>
  );

  const contentProps = {
    loading: detailLoading,
    detail,
    diningTableName,
    paymentMethodNames,
    paymentMethodNamesLoaded,
    canVoid,
    voidLoading,
    onVoidPress,
    canCollectMpQr,
    onCollectMpQr: openMpQrModal,
    canCollectCustomer,
    onCollectCustomer,
    onViewLinkedOrder: detail?.customerOrderId ? onViewLinkedOrder : undefined,
    canReprint,
    reprintLoading,
    onReprintPress,
    onClose,
    onFiscalRetryComplete: () => {
      void refresh();
      onDetailMutated?.();
    },
    showCommissions,
    showAttribution,
    currentUserId: sessionUser?.id ?? null,
    canViewOthersCommissions,
    onNavigateToCommissionPlans: () => {
      onClose();
      (navigation as any).navigate('CommissionPlans');
    },
  };

  if (isMobile) {
    return (
      <>
        <Modal
          visible={visible}
          transparent={false}
          animationType="slide"
          onRequestClose={onClose}
          accessibilityViewIsModal
        >
          <View style={styles.mobileSheet}>
            <View style={[styles.mobileSheetHeader, { paddingTop: insets.top > 0 ? insets.top : spacing.sm }]}>
              <TouchableOpacity
                onPress={onClose}
                style={styles.mobileBackBtn}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel="Volver"
              >
                <Ionicons name="chevron-back" size={20} color={ticketColors.ink2} />
              </TouchableOpacity>
              <Text style={styles.mobileSheetTitle} numberOfLines={1}>
                {detailTitle}
              </Text>
              <TouchableOpacity
                onPress={onClose}
                style={styles.mobileCloseBtn}
                activeOpacity={0.6}
                accessibilityRole="button"
                accessibilityLabel="Cerrar"
              >
                <Ionicons name="close" size={16} color={ticketColors.ink3} />
              </TouchableOpacity>
            </View>

            <SentryErrorBoundary moduleName="Sales"><SaleDetailContent {...contentProps} footerMode="inline" mobileClean /></SentryErrorBoundary>
          </View>
        </Modal>
        {subModals}
      </>
    );
  }

  return (
    <>
      <Modal
        visible={visible}
        transparent
        animationType="fade"
        onRequestClose={onClose}
        accessibilityViewIsModal
      >
        <View style={[posUi.modalBackdrop, modalExperience.modalRoot, backdropPadding]}>
          <View
            style={[
              posUi.modalCard,
              modalShellShared.modalCardShell,
              Platform.OS === 'web'
                ? styles.modalWidthWeb
                : modalExperience.modalWidthStandard,
              { maxHeight: '92%' },
            ]}
          >
            <TicketModalHeader
              kicker="Ventas"
              title={detailTitle}
              subtitle={detailSubtitle ?? undefined}
              onClose={onClose}
            />

            <SentryErrorBoundary moduleName="Sales"><SaleDetailContent {...contentProps} footerMode="inline" railMode /></SentryErrorBoundary>
          </View>
        </View>
      </Modal>
      {subModals}
    </>
  );
}

const styles = StyleSheet.create({
  modalWidthWeb: {
    width: '80%',
    maxWidth: 920,
    minWidth: 420,
  },
  mobileSheet: {
    flex: 1,
    backgroundColor: ticketColors.bg,
  },
  mobileSheetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingBottom: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  mobileBackBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileSheetTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  mobileCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
